import React, { useMemo, useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ViewIcon, Book02Icon, ArrowLeft01Icon, ArrowRight01Icon } from 'hugeicons-react';
import { parseSubcourseOrder } from '../../../utils/subcourseSort';

const shadowVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const indicatorVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const Header = ({ selectedLevel, setSelectedLevel, courseTree, config }) => {
  const { flagSrc, flagAlt, title } = config;

  // Extract subcourse-codes from courseTree dynamically
  const levels = useMemo(() => {
    const levelList = ['Overall'];

    if (!courseTree) {
      console.log('📋 Header: courseTree not loaded yet');
      return levelList;
    }

    // Extract courses array from courseTree
    let courses = [];
    if (Array.isArray(courseTree)) {
      courses = courseTree;
    } else if (courseTree && Array.isArray(courseTree.courses)) {
      courses = courseTree.courses;
    } else if (courseTree && typeof courseTree === 'object') {
      const possibleKeys = ['courses', 'data', 'courseTree', 'tree'];
      for (const key of possibleKeys) {
        if (Array.isArray(courseTree[key])) {
          courses = courseTree[key];
          break;
        }
      }
      if (courses.length === 0) {
        const values = Object.values(courseTree);
        const allArrays = values.filter(v => Array.isArray(v));
        if (allArrays.length > 0) {
          courses = allArrays.flat();
        }
      }
    }

    console.log('📋 Header: Extracting subcourse-codes from courses:', { coursesCount: courses.length });

    // changed by Cursor: urut by field `order` dari courseTree, label = subcourse-code
    const collected = [];
    const seen = new Set();
    for (const course of courses) {
      if (course.subcourses && Array.isArray(course.subcourses)) {
        for (const subcourse of course.subcourses) {
          const subcourseCode = subcourse['subcourse-code'] || subcourse.subcourseCode || subcourse.code;
          if (subcourseCode && !seen.has(subcourseCode)) {
            seen.add(subcourseCode);
            const order = parseSubcourseOrder(subcourse.order);
            collected.push({ code: subcourseCode, order });
            console.log('📋 Header: Found subcourse-code:', subcourseCode, 'order:', order, 'from subcourse:', subcourse.name || subcourse._id);
          }
        }
      }
    }
    collected.sort((a, b) => {
      const orderA = parseSubcourseOrder(a.order) ?? Number.MAX_SAFE_INTEGER;
      const orderB = parseSubcourseOrder(b.order) ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      return (a.code || '').localeCompare(b.code || '');
    });
    const subcourseCodes = collected.map((item) => item.code);

    const finalLevels = [...levelList, ...subcourseCodes];
    console.log('📋 Header: Final levels:', finalLevels);
    return finalLevels;
  }, [courseTree]);

  const displayLevels = useMemo(() => {
    const nonOverall = levels.filter(level => level !== 'Overall');
    return [...nonOverall, 'Overall'];
  }, [levels]);
  const navRef = useRef(null);
  const [showLeftIndicator, setShowLeftIndicator] = useState(false);
  const [showRightIndicator, setShowRightIndicator] = useState(false);

  useEffect(() => {
    const updateOverflowIndicators = () => {
      const navEl = navRef.current;
      if (!navEl || typeof window === 'undefined') {
        return;
      }

      const isMobileView = window.matchMedia('(max-width: 768px)').matches;
      if (!isMobileView) {
        setShowLeftIndicator(false);
        setShowRightIndicator(false);
        return;
      }

      const maxScrollLeft = navEl.scrollWidth - navEl.clientWidth;
      const atLeft = navEl.scrollLeft <= 1;
      const atRight = navEl.scrollLeft >= maxScrollLeft - 1;

      setShowLeftIndicator(!atLeft);
      setShowRightIndicator(!atRight && maxScrollLeft > 0);
    };

    updateOverflowIndicators();

    const navEl = navRef.current;
    if (navEl) {
      navEl.addEventListener('scroll', updateOverflowIndicators, { passive: true });
    }
    window.addEventListener('resize', updateOverflowIndicators);

    return () => {
      if (navEl) {
        navEl.removeEventListener('scroll', updateOverflowIndicators);
      }
      window.removeEventListener('resize', updateOverflowIndicators);
    };
  }, [displayLevels]);

  const handleLevelClick = (level) => {
    console.log('🖱️ Header: Level clicked:', level, 'Current selectedLevel:', selectedLevel);
    setSelectedLevel(level);
  };

  return (
    <div className="m-0 p-0 w-full box-border">
      <div className="bg-gradient-to-br from-[#12766d] via-[#236463] to-[#1d4746] dark:from-[#0d4f49] dark:via-[#153938] dark:to-[#0f2928] py-[22px] pl-6 flex items-center gap-3 text-white m-0 w-full box-border max-md:py-[18px] max-md:px-4 max-md:gap-2.5">
        <div className="w-[52px] h-[52px] rounded-[14px] bg-white shrink-0 overflow-hidden dark:border dark:border-white/20">
          <img src={flagSrc} alt={flagAlt} className="object-cover h-full w-full block" />
        </div>
        <h1 className="text-[30px] font-bold text-white m-0 leading-tight max-md:text-xl">{title}</h1>
      </div>

      {/* Level Navigation Bar */}
      <div
        className="level-navigation-wrapper relative bg-gradient-to-br from-[#12766d] via-[#236463] to-[#1d4746] dark:from-[#0d4f49] dark:via-[#153938] dark:to-[#0f2928]"
        data-has-left-overflow={showLeftIndicator || undefined}
        data-has-right-overflow={showRightIndicator || undefined}
      >
        <div
          className="level-navigation bg-gradient-to-br from-[#12766d] via-[#236463] to-[#1d4746] dark:from-[#0d4f49] dark:via-[#153938] dark:to-[#0f2928] px-6 flex gap-1.5 m-0 w-full box-border max-md:px-0 max-md:overflow-x-auto max-md:overflow-y-hidden max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden"
          ref={navRef}
        >
          {displayLevels.map((level) => (
            <button
              key={level}
              className={`flex items-center gap-2 py-2.5 px-[18px] bg-transparent border-none font-medium cursor-pointer transition-all duration-300 border-b-[3px] border-b-transparent text-sm text-white/70 hover:text-white/90 hover:bg-white/5 max-md:py-2.5 max-md:px-4 max-md:text-[13px] max-md:shrink-0 max-md:whitespace-nowrap max-md:[&>span]:ml-1 ${level === 'Overall' ? 'ml-auto max-md:ml-0 max-md:order-first' : ''} ${selectedLevel === level ? '!text-white !bg-white/10 !border-b-[#fcb319] dark:!bg-white/[0.08]' : ''}`}
              onClick={() => handleLevelClick(level)}
            >
              {level === 'Overall' ? (
                <ViewIcon size={18} strokeWidth={1.8} />
              ) : (
                <Book02Icon size={18} strokeWidth={1.8} />
              )}
              <span>{level === 'Overall' ? 'How ZenVokus works' : level}</span>
            </button>
          ))}
        </div>
        <AnimatePresence initial={false}>
          {showLeftIndicator && (
            <motion.div
              key="shadow-left"
              className="level-nav-shadow max-md:absolute max-md:top-0 max-md:bottom-0 max-md:w-10 max-md:z-[1] max-md:pointer-events-none max-md:left-0 max-md:bg-[linear-gradient(90deg,#1d4746_0%,#236463_40%,transparent_100%)] dark:max-md:bg-[linear-gradient(90deg,rgba(0,0,0,0.55)_0%,rgba(0,0,0,0.25)_50%,transparent_100%)]"
              variants={shadowVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.15, ease: 'easeOut' }}
            />
          )}
          {showRightIndicator && (
            <motion.div
              key="shadow-right"
              className="level-nav-shadow max-md:absolute max-md:top-0 max-md:bottom-0 max-md:w-10 max-md:z-[1] max-md:pointer-events-none max-md:right-0 max-md:bg-[linear-gradient(270deg,#1d4746_0%,#236463_40%,transparent_100%)] dark:max-md:bg-[linear-gradient(270deg,rgba(0,0,0,0.55)_0%,rgba(0,0,0,0.25)_50%,transparent_100%)]"
              variants={shadowVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.15, ease: 'easeOut' }}
            />
          )}
        </AnimatePresence>
        <AnimatePresence initial={false}>
          {showLeftIndicator && (
            <motion.span
              key="indicator-left"
              className="level-nav-indicator hidden max-md:flex max-md:absolute max-md:top-0 max-md:bottom-0 max-md:w-9 max-md:items-center max-md:z-[2] max-md:pointer-events-none max-md:left-0 max-md:justify-start max-md:pl-1.5 max-md:text-white/95"
              aria-hidden
              variants={indicatorVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <ArrowLeft01Icon size={14} strokeWidth={2} />
            </motion.span>
          )}
          {showRightIndicator && (
            <motion.span
              key="indicator-right"
              className="level-nav-indicator hidden max-md:flex max-md:absolute max-md:top-0 max-md:bottom-0 max-md:w-9 max-md:items-center max-md:z-[2] max-md:pointer-events-none max-md:right-0 max-md:justify-end max-md:pr-1.5 max-md:text-white/95"
              aria-hidden
              variants={indicatorVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <ArrowRight01Icon size={14} strokeWidth={2} />
            </motion.span>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Header;
